import { Tabs, Button, Form, Input, Upload, Avatar, message, Switch, Row, Col } from "antd";
import { RevButton } from "../../components/Button";
import MainLayout from "../../layouts/Layout";
import { useNavigate } from "react-router-dom";
import { UserOutlined, PlusOutlined, UploadOutlined, DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { useAtomValue } from "jotai";
import { userAtom } from "../../store/atoms";
import { useSecureService } from "../../store/service";
import { useState, useEffect } from "react";
import styles from "./settings.module.scss";

const Settings = () => {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const { editUser } = useSecureService();
  
  // Sadece Admin ve SuperAdmin kullanıcı ekleyebilir
  const canAddUser = user && ['Admin', 'SuperAdmin'].includes(user.role);
  
  // Form state'leri
  const [profileForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [emailNotification, setEmailNotification] = useState(true);
  const [loadingUserData, setLoadingUserData] = useState(true);

  // Kullanıcı bilgilerini yükle
  useEffect(() => {
    if (user) {
      setUserData(user);
      profileForm.setFieldsValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        title: user.title || ''
      });
      
      // Email notification ayarını API'den oku
      setEmailNotification((user as any).notificationToEmail ?? true);
      
      setLoadingUserData(false);
    } else {
      setLoadingUserData(true);
    }
  }, [user, profileForm]);

  // Profil güncelleme fonksiyonu
  const handleProfileUpdate = async (values: any) => {
    setLoading(true);
    try {
      const updateData: any = {};
      
      if (values.firstName) updateData.firstName = values.firstName;
      if (values.lastName) updateData.lastName = values.lastName;
      if (values.title) updateData.title = values.title;
      if (avatarFile) updateData.thumbnail = avatarFile;

      // Password alanları varsa ekle (sadece ikisi de doldurulduğunda)
      if (values.oldPassword && values.newPassword && values.confirmPassword) {
        updateData.oldPassword = values.oldPassword;
        updateData.newPassword = values.newPassword;
      }

      // Email notification ayarını API'ye gönder (backend hazır olduğunda çalışacak)
      updateData.notificationToEmail = emailNotification;

      const response = await editUser(updateData);
      
      if (response.success) {
        message.success('Profile updated successfully.');
        // Password alanlarını temizle
        profileForm.setFieldsValue({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // User bilgileri otomatik olarak güncellenecek
      } else {
        message.error('An error occurred while updating profile.');
      }
    } catch (error) {
      message.error('An error occurred while updating profile.');
    } finally {
      setLoading(false);
    }
  };


  // Avatar yükleme fonksiyonu
  const handleAvatarUpload = (file: File) => {
    setAvatarFile(file);
    return false; // Ant Design'ın otomatik yüklemesini engelle
  };

  // Avatar silme fonksiyonu
  const handleAvatarDelete = () => {
    setAvatarFile(null);
    // TODO: API'ye avatar silme isteği gönder
    // Mevcut avatar'ı da temizle
    if (userData) {
      setUserData({
        ...userData,
        thumbnailUrl: null
      });
    }
    message.success('Avatar deleted.');
  };

  // Email notification değişikliği
  const handleEmailNotificationChange = (checked: boolean) => {
    setEmailNotification(checked);
    // Değişiklik hemen kayıt edilsin mi yoksa Save Changes butonuyla mı kaydedilsin?
    // Şimdilik state'i güncelleyip, Save Changes ile kaydedilmesini sağlayalım
  };

  const tabItems = [
    {
      key: "user",
      label: "User Settings",
      children: (
        <div className={styles.userSettingsContent}>
          <Row gutter={24}>
            {/* Avatar bölümü - Üst satır */}
            <Col span={24} className={styles.avatarSection}>
              <div className={styles.avatarWrapper}>
                {loadingUserData ? (
                  <div className={styles.loadingWrapper}>
                    <div className={styles.loadingText}>Loading...</div>
                  </div>
                ) : (
                  <>
                    <Avatar 
                      size={48} 
                      src={avatarFile ? URL.createObjectURL(avatarFile) : userData?.thumbnailUrl}
                      icon={<UserOutlined />}
                    />
                    <div className={styles.avatarButtons}>
                      {/* Sadece mevcut avatar varsa Delete butonu göster */}
                      {(userData?.thumbnailUrl || avatarFile) && (
                        <Button 
                          icon={<DeleteOutlined />} 
                          size="small"
                          onClick={handleAvatarDelete}
                          className={styles.customDeleteButton}
                        >
                          Delete
                        </Button>
                      )}
                      <Upload
                        beforeUpload={handleAvatarUpload}
                        showUploadList={false}
                        accept="image/*"
                      >
                        <RevButton 
                          icon={<UploadOutlined />} 
                          className={styles.customUploadButton}
                        >
                          Upload
                        </RevButton>
                      </Upload>
                    </div>
                  </>
                )}
              </div>
            </Col>

            {/* Form alanları */}
            <Col span={24}>
              <Form
                form={profileForm}
                layout="vertical"
                onFinish={handleProfileUpdate}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Name"
                      name="firstName"
                      rules={[{ required: true, message: 'Name is required!' }]}
                    >
                      <Input placeholder="Input" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Surname"
                      name="lastName"
                      rules={[{ required: true, message: 'Surname is required!' }]}
                    >
                      <Input placeholder="Input" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Title"
                      name="title"
                    >
                      <Input placeholder="Input" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Password alanları */}
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Old Password"
                      name="oldPassword"
                    >
                      <Input.Password placeholder="Enter old password" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="New Password"
                      name="newPassword"
                      rules={[
                        { min: 6, message: 'Password must be at least 6 characters!' }
                      ]}
                    >
                      <Input.Password placeholder="Enter new password" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label="Re-enter New Password"
                      name="confirmPassword"
                      dependencies={['newPassword']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const newPassword = getFieldValue('newPassword');
                            if (!newPassword) {
                              return Promise.resolve();
                            }
                            if (!value) {
                              return Promise.reject(new Error('Please confirm your new password!'));
                            }
                            if (newPassword === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="Re-enter new password" />
                    </Form.Item>
                  </Col>
                </Row>
                <div className={styles.notificationSection}>
                  <span className={styles.notificationLabel}>Notification to email:</span>
                  <Switch 
                    checked={emailNotification} 
                    onChange={handleEmailNotificationChange}
                    style={{ backgroundColor: emailNotification ? 'var(--app-accent-color)' : '#666' }}
                  />
                </div>

                <div className={styles.saveButtonWrapper}>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save Changes
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <MainLayout title="Settings" icon={<SettingOutlined />}>
      <div className={styles.settingsContainer}>
        <div className={styles.tabsWrapper}>
          <Tabs
            defaultActiveKey="user"
            items={tabItems}
            tabBarExtraContent={
              canAddUser && (
                <Button
                  type="primary"
                  onClick={() => navigate("/user/add")}
                  icon={<PlusOutlined />}
                >
                  New User
                </Button>
              )
            }
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default Settings;